import type {
    ComposableOptionsBase,
    Sku
} from '~/types';
import type {
    PaginatedPayload,
    CustomPagination,
    PaginatedRequest,
    ApiQueryParams
} from 'x1-base/types';

import { merge as lo_mergeCompat } from 'es-toolkit/compat';
import { omit as lo_omit } from 'es-toolkit';

import type { NitroFetchOptions } from 'nitropack';
import { useApiUtils } from 'x1-base/composables';
import { useCustomFetch, useDebugExec } from '~/composables';

// NOTE: This will replace useSkus (rename to match)

export interface UseSkusOptions extends ComposableOptionsBase {}
export function useSkus(options: Partial<UseSkusOptions> = {}) {
    const apiBasePath = '/v1/skus';
    const useApiUtilsObj = useApiUtils();
    const $_fetch = useCustomFetch({ siteContext: options.siteContext });

    async function getItems(
        args: PaginatedRequest = {},
        query: NitroFetchOptions<string>['query'] = {}
    ): Promise<PaginatedPayload<Sku> | Error> {
        const url = args?.url ?? `${apiBasePath}/`;

        const config: NitroFetchOptions<string> = {
            method: 'GET',
            query: {}
        };

        lo_mergeCompat(config.query, args?.params, query, {
            // Base params here
        });

        let paginatedPayload: PaginatedPayload<Sku>;

        try {
            const response = await $_fetch<CustomPagination<Sku>>(url, config);

            paginatedPayload = {
                pagination: lo_omit(response, ['results']),
                data: response.results
            };

            return paginatedPayload;
        } catch (err) {
            return useApiUtilsObj.getErrorObj(err);
        }
    }

    async function exportDownload(
        args: PaginatedRequest = {},
        query: NitroFetchOptions<string>['query'] = {},
        filename?: string
    ): Promise<void | Error> {
        if (!import.meta.client) {
            return;
        }

        const url = args?.url ?? `${apiBasePath}/`;

        const config: NitroFetchOptions<string> = {
            method: 'GET',
            responseType: 'blob',
            headers: {
                'Accept': 'text/csv'
            },
            query: {}
        };

        // TODO: Fix types
        lo_mergeCompat(
            config.query,
            lo_omit(
                (args?.params || {} as any),
                [
                    'page',
                    'page_size'
                ]
            ),
            query, {
                all: 'true'
            }
        );

        if (!filename) {
            filename = `skus-${new Date().toLocaleString().replace(/[\/:,\s]/g, '-')}.csv`;
        }

        try {
            const response = await $_fetch.raw(url, config);
            const blob = response._data as Blob;
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.setAttribute('download', filename);
            a.href = downloadUrl;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(downloadUrl);
        } catch (err) {
            console.info('ERROR', err);
            return useApiUtilsObj.getErrorObj(err);
        }
    }

    async function getItem(uid: string, expand: string[] = []): Promise<Sku | Error> {
        const url = `${apiBasePath}/${uid}/`;
        const config: NitroFetchOptions<string> = {
            method: 'GET',
            query: { expand: expand.join(',') }
        };

        try {
            const response = await $_fetch<Sku>(url, config);
            return response;
        } catch (err) {
            return useApiUtilsObj.getErrorObj(err);
        }
    }

    return {
        getItems,
        exportDownload,
        getItem
    };
}
